import { useEffect, useState } from "react";

/**
 * Hook that returns true once the page has fully loaded.
 *
 * Listens for the browser's `window.load` event, which fires when all
 * resources (images, scripts, stylesheets) have finished loading.
 *
 * Useful for deferring animations or heavy operations until after
 * Core Web Vitals measurements (like LCP) have completed.
 *
 * @returns boolean - true once `window.load` has fired, false otherwise
 */
export function usePageLoaded(): boolean {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const handleLoad = () => setIsLoaded(true);

    // If page already loaded (e.g., SPA navigation), trigger immediately
    if (document.readyState === "complete") {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
      handleLoad();
      return;
    }

    window.addEventListener("load", handleLoad);
    return () => window.removeEventListener("load", handleLoad);
  }, []);

  // Treat undefined (initial SSR state) as false
  return isLoaded ?? false;
}
